import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, GripVertical, Plus, Sparkles } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { Category, CourseListItem, GeneratedContent, KnowledgeModule, LabStep } from '../../types/content';
import Button from '../../components/UI/Button';
import ActionStatus from '../../components/UI/ActionStatus';
import Input from '../../components/UI/Input';
import BlockEditor, { blocksFromPlainText, emptyBlock } from '../../components/content/BlockEditor';
import LoadingOverlay, { PageLoading } from '../../components/UI/LoadingOverlay';
import { UnsavedLeaveDialog, useDirtyBaseline, useUnsavedChangesGuard } from '../../components/content/UnsavedLeaveDialog';
import { labDraftFromGenerated } from '../../utils/labFromGenerated';
import { useAutoClear } from '../../hooks/useAutoClear';
import { useAuth } from '../../context/AuthContext';
import { submitMessage } from '../../utils/roles';
import ContentOwnerSelect from '../../components/content/ContentOwnerSelect';
import { focusPageTop } from '../../utils/focusPageTop';
import { openLearnerTab } from '../../utils/openLearnerTab';
import { getUserProfileId } from '../../utils/userProfile';


const emptyStep = (order: number): LabStep => ({
  title: `Step ${order}`,
  instruction: '',
  expectedResult: '',
  codeSample: '',
  language: '',
  notes: '',
  displayOrder: order,
  blocks: [emptyBlock(1)],
});

const LabEditorPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const labId = id ? Number(id) : undefined;
  const [name, setName] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [courseId, setCourseId] = useState<number | ''>(searchParams.get('courseId') ? Number(searchParams.get('courseId')) : '');
  const [courseTopicId, setCourseTopicId] = useState<number | ''>(searchParams.get('topicId') ? Number(searchParams.get('topicId')) : '');
  const [technologyName, setTechnologyName] = useState('');
  const [difficulty, setDifficulty] = useState('Beginner');
  const [duration, setDuration] = useState('');
  const [prerequisites, setPrerequisites] = useState('');
  const [outcomes, setOutcomes] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [authorId, setAuthorId] = useState<number | ''>('');
  const [authorLabel, setAuthorLabel] = useState('');
  const [steps, setSteps] = useState<LabStep[]>([emptyStep(1)]);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | 'details'>('details');
  const [categories, setCategories] = useState<Category[]>([]);
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [courseModules, setCourseModules] = useState<KnowledgeModule[]>([]);
  const [technologies, setTechnologies] = useState<Category[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 10000);
  const [aiTopic, setAiTopic] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [loading, setLoading] = useState(!!labId);
  const [workflowStatus, setWorkflowStatus] = useState<string | null>(null);
  const [labSlug, setLabSlug] = useState('');

  useEffect(() => {
    contentApi.listCategories('Course').then((r) => setCategories(r.data.categories || [])).catch(() => undefined);
    contentApi.listCategories('Technology').then((r) => setTechnologies(r.data.categories || [])).catch(() => undefined);
    Promise.all([
      contentApi.myCourses({ pageSize: 100 }).catch(() => ({ data: { courses: [] as CourseListItem[] } })),
      contentApi.listCourses({ pageSize: 50, sort: 'catalog' }).catch(() => ({ data: { courses: [] as CourseListItem[] } })),
    ]).then(([mine, published]) => {
      const map = new Map<number, CourseListItem>();
      [...(published.data.courses || []), ...(mine.data.courses || [])].forEach((item) => map.set(item.id, item));
      setCourses([...map.values()]);
    });
  }, []);

  useEffect(() => {
    if (!labId) return;
    setLoading(true);
    contentApi.getLab(labId).then((r) => {
      const c = r.data.lab;
      if (!c) return;
      setName(c.name);
      setSubtitle(c.subtitle || '');
      setDescription(c.description || '');
      setCategoryId(c.categoryId || '');
      setCourseId(c.courseId || '');
      setCourseTopicId(c.courseTopicId || '');
      setTechnologyName(c.technologyName || '');
      setDifficulty(c.difficulty);
      setDuration(c.duration || '');
      setPrerequisites(c.prerequisites || '');
      setOutcomes(c.learningOutcomes || '');
      setIsPublic(!!c.isPublic);
      setAuthorId(c.author?.authorId || '');
      setAuthorLabel(c.author?.displayName || '');
      setWorkflowStatus(c.status || null);
      setLabSlug(c.slug || '');
      const loaded = (c.steps && c.steps.length)
        ? c.steps.map((s, i) => ({
          ...s,
          displayOrder: s.displayOrder || i + 1,
          blocks: (s.blocks && s.blocks.length)
            ? s.blocks
            : [
              ...blocksFromPlainText(s.instruction),
              ...(s.codeSample
                ? [{ type: 'Code' as const, content: s.codeSample, language: s.language || '', displayOrder: 2 }]
                : []),
            ],
        }))
        : (c.modules || []).flatMap((m) => (m.topics || []).map((t, i) => ({
          title: t.title,
          instruction: (t.blocks || []).filter((b) => b.type !== 'code').map((b) => b.content || '').join('\n\n'),
          codeSample: (t.blocks || []).find((b) => b.type === 'code')?.content || '',
          language: (t.blocks || []).find((b) => b.type === 'code')?.language || '',
          expectedResult: (t.blocks || []).find((b) => b.expectedResult)?.expectedResult || '',
          notes: t.objective || '',
          displayOrder: i + 1,
          blocks: (t.blocks && t.blocks.length) ? t.blocks : [emptyBlock(1)],
        })));
      setSteps(loaded.length ? loaded : [emptyStep(1)]);
      setSelected('details');
    }).catch(() => setError('Unable to load lab.')).finally(() => setLoading(false));
  }, [labId]);

  useEffect(() => {
    if (labId) return;
    const profileId = getUserProfileId(user);
    if (!profileId) return;
    setAuthorId(profileId);
    setAuthorLabel(user?.username || user?.email || '');
  }, [labId, user]);

  useEffect(() => {
    if (!courseId) {
      setCourseModules([]);
      return;
    }
    contentApi.getCourse(Number(courseId)).then((r) => {
      const loaded = r.data.course;
      if (!loaded) return;
      setCourseModules(loaded.modules || []);
      if (!categoryId && loaded.categoryId) setCategoryId(loaded.categoryId);
    }).catch(() => undefined);
  }, [courseId, categoryId]);

  const payload = useMemo(() => ({
    name,
    subtitle,
    description,
    categoryId: categoryId || null,
    technologyId: technologies.find((t) => t.name.toLowerCase() === technologyName.trim().toLowerCase())?.id || null,
    technologyName: technologyName.trim() || null,
    difficulty,
    duration,
    prerequisites,
    learningOutcomes: outcomes,
    isPublic,
    authorId: authorId || null,
    courseTopicId: courseTopicId || null,
    steps: steps.map((s, i) => ({
      ...s,
      displayOrder: i + 1,
      instruction: (s.blocks || []).filter((b) => (b.type || '').toLowerCase() !== 'code').map((b) => b.content || '').filter(Boolean).join('\n\n') || s.instruction,
      codeSample: (s.blocks || []).find((b) => (b.type || '').toLowerCase() === 'code')?.content || s.codeSample,
      language: (s.blocks || []).find((b) => (b.type || '').toLowerCase() === 'code')?.language || s.language,
      blocks: (s.blocks || []).map((b, n) => ({ ...b, displayOrder: n + 1 })),
    })),
  }), [name, subtitle, description, categoryId, technologyName, technologies, difficulty, duration, prerequisites, outcomes, isPublic, authorId, courseTopicId, steps]);

  const { isDirty, markClean } = useDirtyBaseline(payload, !loading);
  const { leaveOpen, stay, leaveWithoutSaving } = useUnsavedChangesGuard(isDirty);

  const save = async (submit = false) => {
    setSaving(true);
    setError(null);
    try {
      const response = await contentApi.saveLab(payload, labId);
      const savedId = response.data.lab?.id;
      if (!savedId) throw new Error('Save failed.');
      setLabSlug(response.data.lab?.slug || labSlug);
      setWorkflowStatus(response.data.lab?.status || workflowStatus);
      if (submit) {
        const submitted = await contentApi.submitLab(savedId);
        setWorkflowStatus(submitted.data.lab?.status || workflowStatus);
        setMessage(submitMessage(user));
      } else {
        setMessage('Draft saved.');
      }
      markClean();
      if (!labId) {
        const fromCourse = searchParams.get('courseId');
        navigate(fromCourse ? `/my-courses/${fromCourse}/edit` : `/admin/labs/${savedId}/edit`, { replace: true });
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const applyGenerated = (content: GeneratedContent) => {
    const draft = labDraftFromGenerated(content);
    if (draft.name) setName(draft.name);
    if (draft.subtitle) setSubtitle(draft.subtitle);
    if (draft.description) setDescription(draft.description);
    if (draft.duration) setDuration(draft.duration);
    if (draft.prerequisites) setPrerequisites(draft.prerequisites);
    if (draft.outcomes) setOutcomes(draft.outcomes);
    setSteps(draft.steps);
    setSelected('details');
    setAiOpen(false);
    setMessage('AI drafted the lab. Review the name, details, and steps before saving.');
  };

  const generate = async () => {
    const topic = aiTopic.trim();
    if (!topic) {
      setError('Enter a lab topic for AI to generate from.');
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const response = await contentApi.generate({
        topic,
        prompt: aiPrompt.trim(),
        action: 'generate',
        difficulty,
        generationType: 'lab',
        targetLabId: labId,
      });
      if (!response.data.content) {
        throw new Error(response.data.message || 'Generation failed.');
      }
      applyGenerated(response.data.content);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const moveStep = (from: number, to: number) => {
    if (to < 0 || to >= steps.length || from === to) return;
    setSteps((all) => {
      const next = [...all];
      const [row] = next.splice(from, 1);
      next.splice(to, 0, row);
      return next;
    });
    setSelected((cur) => {
      if (cur === 'details') return cur;
      if (cur === from) return to;
      if (from < cur && to >= cur) return cur - 1;
      if (from > cur && to <= cur) return cur + 1;
      return cur;
    });
  };

  const selectedStep = selected === 'details' ? undefined : steps[selected];
  const backTo = searchParams.get('courseId')
    ? `/my-courses/${searchParams.get('courseId')}/edit`
    : '/admin/dashboard?tab=labs';
  const topics = courseModules.flatMap((module) => module.topics.map((topic) => ({ ...topic, moduleTitle: module.title })));

  if (loading) return <PageLoading label="Loading lab…" />;

  return (
    <div id="lab-editor-top" tabIndex={-1} className="relative max-w-7xl mx-auto px-4 py-10 outline-none">
      {generating && <LoadingOverlay />}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <Link to={backTo} className="inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {searchParams.get('courseId') ? 'Back to course' : 'Back to labs'}
          </Link>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">{labId ? 'Edit lab' : 'New lab'}</h1>
          {workflowStatus && <p className="mt-1 text-sm text-gray-500">Status: {workflowStatus}</p>}
          <p className="text-sm text-gray-600 mt-1">A lab is one practice task, written as a sequence of steps and linked to a course topic.</p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex gap-2">
          {labSlug ? (
            <Button variant="outline" type="button" onClick={() => openLearnerTab(`/labs/${labSlug}`)}>View</Button>
          ) : null}
          <Button variant="secondary" type="button" onClick={() => save(false)} disabled={saving}>Save draft</Button>
          <Button type="button" onClick={() => save(true)} disabled={saving}>Submit for review</Button>
          </div>
          <ActionStatus className="w-full max-w-md text-right" message={message} error={error} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[18rem_minmax(0,1fr)] gap-6 items-start">
        <nav className="lg:sticky lg:top-24 rounded-2xl border border-gray-200 bg-white p-3">
          <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Steps</p>
          <button
            type="button"
            className={`w-full text-left rounded-lg px-3 py-2 text-sm ${selected === 'details' ? 'bg-primary-50 text-primary-800 font-semibold' : 'text-gray-800 hover:bg-gray-50'}`}
            onClick={() => setSelected('details')}
          >
            Lab details
          </button>
          <ul className="mt-2 space-y-1">
            {steps.map((step, i) => (
              <li
                key={step.id || `step-${i}`}
                className={`rounded-lg ${selected === i ? 'bg-primary-50' : 'hover:bg-gray-50'}`}
                draggable
                onDragStart={() => setDragFrom(i)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => {
                  if (dragFrom != null) moveStep(dragFrom, i);
                  setDragFrom(null);
                }}
              >
                <div className="flex items-center gap-1">
                  <button type="button" className="flex-1 text-left px-3 py-2 text-sm flex items-center gap-2" onClick={() => setSelected(i)}>
                    <GripVertical className="h-3.5 w-3.5 text-gray-400" />
                    {i + 1}. {step.title || 'Untitled step'}
                  </button>
                  <button type="button" className="px-1 text-xs text-gray-500" aria-label="Move step up" onClick={() => moveStep(i, i - 1)}>↑</button>
                  <button type="button" className="px-1 text-xs text-gray-500" aria-label="Move step down" onClick={() => moveStep(i, i + 1)}>↓</button>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3 space-y-2">
            <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => {
              setSteps((all) => [...all, emptyStep(all.length + 1)]);
              setSelected(steps.length);
              requestAnimationFrame(() => focusPageTop('lab-editor-top'));
            }}>
              <Plus className="h-4 w-4 mr-1" /> Add step
            </Button>
            <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => {
              if (!aiTopic.trim() && name.trim()) setAiTopic(name.trim());
              setAiOpen(true);
            }}>
              <Sparkles className="h-4 w-4 mr-1" /> Ask AI to generate steps
            </Button>
          </div>
        </nav>

        <div className="min-w-0 space-y-6">
          {selected === 'details' && (
            <div className="space-y-6 rounded-2xl border border-gray-200 bg-white p-5">
              <Input id="name" label="Lab name" value={name} onChange={(e) => setName(e.target.value)} />
              <ContentOwnerSelect value={authorId} currentLabel={authorLabel} onChange={setAuthorId} />
              <Input id="subtitle" label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
              <label className="block text-sm font-medium text-gray-700">Description
                <textarea className="mt-1 w-full rounded-md border-gray-300" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
              </label>
              <div className="grid md:grid-cols-2 gap-4">
                <label className="block text-sm font-medium text-gray-700">Category
                  <select className="mt-1 w-full rounded-md border-gray-300" value={categoryId} onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">Select</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-medium text-gray-700">Course
                  <select className="mt-1 w-full rounded-md border-gray-300" value={courseId} onChange={(e) => setCourseId(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">Select</option>
                    {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-medium text-gray-700">Topic
                  <select className="mt-1 w-full rounded-md border-gray-300" value={courseTopicId} onChange={(e) => setCourseTopicId(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">Select a topic to link</option>
                    {topics.map((t) => <option key={t.id} value={t.id}>{t.moduleTitle} · {t.title}</option>)}
                  </select>
                </label>
                <Input
                  id="technology"
                  label="Technology"
                  value={technologyName}
                  onChange={(e) => setTechnologyName(e.target.value)}
                  placeholder="e.g. React, SQL Server"
                />
                <label className="block text-sm font-medium text-gray-700">Difficulty
                  <select className="mt-1 w-full rounded-md border-gray-300" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                    <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
                  </select>
                </label>
                <Input id="duration" label="Duration" value={duration} onChange={(e) => setDuration(e.target.value)} />
              </div>
              <label className="block text-sm font-medium text-gray-700">Prerequisites
                <textarea className="mt-1 w-full rounded-md border-gray-300" rows={2} value={prerequisites} onChange={(e) => setPrerequisites(e.target.value)} />
              </label>
              <label className="block text-sm font-medium text-gray-700">Outcomes
                <textarea className="mt-1 w-full rounded-md border-gray-300" rows={2} value={outcomes} onChange={(e) => setOutcomes(e.target.value)} />
              </label>
              <label className="inline-flex items-start gap-2 text-sm text-gray-700">
                <input className="mt-1" type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                <span>Show on Labs menu (public). Unchecked labs are only reachable from the linked course journey.</span>
              </label>
            </div>
          )}

          {selectedStep && selected !== 'details' && (
            <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5">
              <div className="flex justify-between">
                <h2 className="text-lg font-semibold">Step {selected + 1}</h2>
                <Button type="button" variant="ghost" onClick={() => {
                  setSteps((all) => all.filter((_, i) => i !== selected));
                  setSelected('details');
                }}>Remove step</Button>
              </div>
              <Input id="step-title" label="Step title" value={selectedStep.title} onChange={(e) => setSteps((all) => all.map((s, i) => i === selected ? { ...s, title: e.target.value } : s))} />
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Instruction</p>
                <BlockEditor
                  blocks={selectedStep.blocks?.length ? selectedStep.blocks : blocksFromPlainText(selectedStep.instruction)}
                  uploadContext={{
                    labId,
                    catalogKind: 'labs',
                    entityName: name,
                    topicName: selectedStep.title || `step-${selected + 1}`,
                  }}
                  onChange={(blocks) => setSteps((all) => all.map((s, i) => i === selected ? { ...s, blocks } : s))}
                />
              </div>
              <Input id="lang" label="Language (for code blocks)" value={selectedStep.language || ''} onChange={(e) => setSteps((all) => all.map((s, i) => i === selected ? { ...s, language: e.target.value } : s))} />
              <label className="block text-sm font-medium text-gray-700">Expected result
                <textarea className="mt-1 w-full rounded-md border border-gray-300" rows={3} value={selectedStep.expectedResult || ''} onChange={(e) => setSteps((all) => all.map((s, i) => i === selected ? { ...s, expectedResult: e.target.value } : s))} />
              </label>
              <label className="block text-sm font-medium text-gray-700">Notes
                <textarea className="mt-1 w-full rounded-md border border-gray-300" rows={2} value={selectedStep.notes || ''} onChange={(e) => setSteps((all) => all.map((s, i) => i === selected ? { ...s, notes: e.target.value } : s))} />
              </label>
            </div>
          )}
        </div>
      </div>
      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-6">
        {workflowStatus ? <p className="text-sm text-gray-500">Status: {workflowStatus}</p> : <span />}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {labSlug ? (
            <Button variant="outline" type="button" onClick={() => openLearnerTab(`/labs/${labSlug}`)}>View</Button>
          ) : null}
          <Button variant="secondary" type="button" onClick={() => save(false)} disabled={saving}>Save draft</Button>
          <Button type="button" onClick={() => save(true)} disabled={saving}>Submit for review</Button>
        </div>
      </div>
      {aiOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <p className="text-lg font-semibold text-gray-900">Ask AI to generate this lab</p>
            <p className="mt-1 text-sm text-gray-600">Give the topic. AI fills the lab name, description, and step-by-step practice using the standard lab template.</p>
            <div className="mt-4">
              <Input id="ai-topic" label="Topic" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="e.g. installing Docker on Windows" />
            </div>
            <label className="block text-sm font-medium text-gray-700">
              Extra instructions (optional)
              <textarea
                className="mt-1 w-full rounded-md border border-gray-300"
                rows={3}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Leave blank to use the standard lab template"
              />
            </label>
            {error && <p className="mt-3 text-sm text-error-700">{error}</p>}
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAiOpen(false)} disabled={generating}>Cancel</Button>
              <Button type="button" onClick={generate} disabled={generating}>{generating ? 'Generating…' : 'Generate steps'}</Button>
            </div>
          </div>
        </div>
      )}
      <UnsavedLeaveDialog open={leaveOpen} onStay={stay} onLeave={leaveWithoutSaving} />
    </div>
  );
};

export default LabEditorPage;
